import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Ticket, Calendar, MapPin, Mic2, QrCode, X, Check, Clock, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function MyTicketsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingTicket, setViewingTicket] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [resaleLoading, setResaleLoading] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tickets/my", { params: { upcoming: !showPast } });
      setTickets(res.data.tickets || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, showPast]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleListForResale = async (ticket) => {
    const priceStr = prompt(
      `Prix de revente en EUR (max ${ticket.price > 0 ? ((ticket.price * 1.1) / 100).toFixed(2) : "gratuit"} EUR, 0 = gratuit)`,
      ticket.price > 0 ? (ticket.price / 100).toFixed(2) : "0"
    );
    if (priceStr === null) return;
    const priceCents = Math.round(parseFloat(priceStr) * 100);
    if (isNaN(priceCents) || priceCents < 0) return toast.success("Prix invalide");
    setResaleLoading(ticket.id);
    try {
      await api.post("/resale/list", { ticket_id: ticket.id, price: priceCents });
      toast.success("Billet mis en vente ! Retrouvez-le sur le Marche secondaire.");
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setResaleLoading(null);
  };

  const statusConfig = {
    valid: { label: "Valide", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", icon: Check },
    used: { label: "Utilise", color: "bg-muted text-muted-foreground border-border", icon: Check },
    cancelled: { label: "Annule", color: "bg-red-500/10 text-red-400 border-red-500/20", icon: X },
  };

  return (
    <div className="py-6 space-y-6" data-testid="my-tickets-page">
      <div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
          <Ticket className="w-7 h-7 text-[#FF6B35]" />
          Mes Billets
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{tickets.length} billet{tickets.length > 1 ? "s" : ""}</p>
      </div>

      {/* Toggle past */}
      <div className="flex gap-1 bg-card rounded-full p-1 border border-border">
        <button
          onClick={() => setShowPast(false)}
          className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${!showPast ? "bg-[#FF6B35] text-white" : "text-muted-foreground"}`}
          data-testid="tickets-upcoming"
        >
          A venir
        </button>
        <button
          onClick={() => setShowPast(true)}
          className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${showPast ? "bg-[#FF6B35] text-white" : "text-muted-foreground"}`}
          data-testid="tickets-past"
        >
          Passes
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="bg-card rounded-3xl h-32 animate-pulse" />)}
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16">
          <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">
            {showPast ? "Aucun billet passe" : "Aucun billet a venir"}
          </p>
          <button
            onClick={() => navigate("/billetterie")}
            className="mt-4 bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform"
          >
            Decouvrir les evenements
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const cfg = statusConfig[t.status] || statusConfig.valid;
            const StatusIcon = cfg.icon;
            return (
              <div
                key={t.id}
                className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm"
                data-testid={`ticket-card-${t.id}`}
              >
                {/* Ticket visual */}
                <div className="bg-gradient-to-r from-[#FF6B35] to-[#FF8FA3] p-4 relative">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-background" />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 rounded-full bg-background" />
                  <div className="flex items-center justify-between px-2">
                    <div>
                      <p className="font-heading font-extrabold text-white text-lg leading-tight">{t.event_title}</p>
                      <p className="text-white/80 text-xs mt-0.5">{t.category_name}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-[#FF6B35]" />
                      {new Date(t.event_date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                      {t.event_time && ` ${t.event_time}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mic2 className="w-3 h-3" /> {t.artist_name}
                    </span>
                    {t.event_location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {t.event_location}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">
                      Achete le {new Date(t.purchased_at).toLocaleDateString("fr-FR")}
                      {t.price > 0 && <span className="ml-2 font-semibold text-foreground">{(t.price / 100).toFixed(2)} EUR</span>}
                    </p>
                    {t.status === "valid" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleListForResale(t)}
                          disabled={resaleLoading === t.id}
                          className="flex items-center gap-1.5 bg-card border border-border text-muted-foreground font-medium px-3 py-2 rounded-full text-xs hover:border-[#FF6B35]/40 hover:text-[#FF6B35] transition-colors disabled:opacity-50"
                          data-testid={`resale-btn-${t.id}`}
                        >
                          <Repeat className="w-3.5 h-3.5" />
                          {resaleLoading === t.id ? "..." : "Revendre"}
                        </button>
                        <button
                          onClick={() => setViewingTicket(t)}
                          className="flex items-center gap-1.5 bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform"
                          data-testid={`show-qr-${t.id}`}
                        >
                          <QrCode className="w-4 h-4" />
                          Mon QR Code
                        </button>
                      </div>
                    )}
                    {t.status === "used" && t.scanned_at && (
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Check className="w-3 h-3" /> Scanne le {new Date(t.scanned_at).toLocaleDateString("fr-FR")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* QR Code viewer modal */}
      {viewingTicket && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" data-testid="qr-viewer-modal">
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-foreground">Mon Billet</h3>
              <button onClick={() => setViewingTicket(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-white rounded-2xl p-4 inline-block mx-auto">
              <img
                src={`data:image/png;base64,${viewingTicket.qr_base64}`}
                alt="QR Code"
                className="w-48 h-48"
                data-testid="ticket-qr-image"
              />
            </div>

            <div>
              <p className="font-heading font-bold text-foreground">{viewingTicket.event_title}</p>
              <p className="text-sm text-muted-foreground">{viewingTicket.category_name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(viewingTicket.event_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                {viewingTicket.event_time && ` a ${viewingTicket.event_time}`}
              </p>
              {viewingTicket.event_location && (
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" /> {viewingTicket.event_location}
                </p>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground">Presentez ce QR code a l'entree</p>
          </div>
        </div>
      )}
    </div>
  );
}
